import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const INVENTARIO = "Inventario non disponibile: riprova."
const INTERROTTA = "Importazione interrotta: i membri già acquisiti restano salvati; puoi riprovare gli altri."
const NON_RILETTA = "I membri sono stati acquisiti, ma la scheda non si è aggiornata. Ricaricala per vederli."

// components/verifica/InventarioZip.js. Lo stato di React (archivio letto, filtro, fase, scelti, acquisiti, errore,
// avanzamento) sta qui e nella pagina:
// - «Leggi l’inventario dello ZIP» / «Rileggi questo archivio»: POST leggi {sha256}; la risposta è la parte con
//   l'archivio (_inventario_zip_archivio), che prende il posto della precedente; filtro e fase restano, i scelti no;
//   un errore resta un errore (mai «Lettura completa.»): il dettaglio o «Inventario non disponibile: riprova.»;
// - i membri leggibili (completi, con indice intero e impronta) si scelgono uno a uno o con «Seleziona i membri
//   leggibili del filtro»; «Importa {n} selezionati come proposte» (solo con la fase) li manda uno alla volta, in
//   ordine d'archivio, con «i di n: nome»; al primo errore si ferma con il dettaglio o il testo dell'interruzione: gli
//   acquisiti restano tali e fuori dai scelti, così un nuovo clic riprova solo gli altri; poi, se qualcosa è cambiato,
//   la scheda si rilegge una volta (onChanged → load(false)).
// Come la chiave React id-doc-sha256: finché l'archivio è alla stessa versione la scheda riletta con morph non riscrive
// questo elemento (né lo chiude).
export default class extends Controller {
  static targets = ["sommario", "errore", "avanzamento", "campi", "leggi", "archivio", "filtro", "fase", "importa", "membro"]
  static values = { leggiUrl: String, importaUrl: String, schedaUrl: String, sha256: String }

  connect() {
    this.vivo = true
    this.scelti = new Set()
    this.acquisiti = new Set()
    this.lettura = false
    this.aggiorna()
  }

  disconnect() {
    this.vivo = false
  }

  trattieni(event) {
    if (event.target !== this.element) return
    if (event.detail.newElement?.getAttribute?.("data-inventario-zip-sha256-value") === this.element.getAttribute("data-inventario-zip-sha256-value")) {
      event.preventDefault()
    }
  }

  attributo(event) {
    if (event.target === this.element && event.detail.attributeName === "open") event.preventDefault()
  }

  sha256ValueChanged(_nuovo, vecchio) {
    if (vecchio === undefined || !this.scelti) return
    this.scelti.clear()
    this.acquisiti.clear()
  }

  get letto() {
    return this.archivioTarget.dataset.letto === "si"
  }

  indice(li) { return Number(li.dataset.indice) }

  usabile(li) { return li.dataset.usabile === "si" }

  visibile(li) {
    const filtro = this.hasFiltroTarget ? this.filtroTarget.value.toLocaleLowerCase() : ""
    return (li.dataset.nome || "").toLocaleLowerCase().includes(filtro)
  }

  filtra() {
    this.aggiorna()
  }

  scegli(event) {
    const i = this.indice(event.target.closest("li"))
    event.target.checked ? this.scelti.add(i) : this.scelti.delete(i)
    this.aggiorna()
  }

  seleziona() {
    this.membroTargets.filter((li) => this.visibile(li) && this.usabile(li)).forEach((li) => this.scelti.add(this.indice(li)))
    this.aggiorna()
  }

  aggiorna() {
    const fase = this.hasFaseTarget ? this.faseTarget.value : ""
    this.membroTargets.forEach((li) => {
      li.hidden = !this.visibile(li)
      const casella = li.querySelector("input[type='checkbox']")
      if (casella) casella.checked = this.scelti.has(this.indice(li))
      const fatto = li.querySelector("[data-acquisito]")
      if (fatto) fatto.hidden = !this.acquisiti.has(this.indice(li))
    })
    if (this.hasImportaTarget) {
      this.importaTarget.textContent = `Importa ${this.scelti.size} selezionati come proposte`
      this.importaTarget.disabled = !fase || !this.scelti.size
    }
    this.leggiTarget.textContent = this.lettura ? "Lettura in corso…" : this.letto ? "Rileggi questo archivio" : "Leggi l’inventario dello ZIP"
  }

  occupato(si) {
    this.campiTarget.disabled = si
  }

  mostraErrore(testo) {
    this.erroreTarget.textContent = testo
    this.erroreTarget.hidden = !testo
  }

  mostraAvanzamento(testo) {
    this.avanzamentoTarget.textContent = testo
    this.avanzamentoTarget.hidden = !testo
  }

  corpo(extra = {}) {
    return { ...(this.hasSha256Value ? { sha256: this.sha256Value } : {}), ...extra }
  }

  async invia(url, corpo, accetta) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const risposta = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: accetta, ...(token ? { "X-CSRF-Token": token } : {}) },
      body: JSON.stringify(corpo),
    })
    // una risposta rediretta (sessione scaduta: la pagina d'accesso) è un errore, non un inventario o un'acquisizione
    if (!risposta.ok || risposta.redirected) {
      let detail = ""
      try { detail = (await risposta.json()).detail || "" } catch { detail = "" }
      throw new Error(detail)
    }
    return risposta
  }

  async leggi() {
    this.lettura = true
    this.occupato(true)
    this.mostraErrore("")
    this.aggiorna()
    try {
      const risposta = await this.invia(this.leggiUrlValue, this.corpo(), "text/html")
      const html = await risposta.text()
      if (!this.vivo) return
      const fase = this.hasFaseTarget ? this.faseTarget.value : ""
      const filtro = this.hasFiltroTarget ? this.filtroTarget.value : ""
      const modello = document.createElement("template")
      modello.innerHTML = html.trim()
      const nuovo = modello.content.firstElementChild
      if (!nuovo?.matches("[data-inventario-zip-target='archivio']")) throw new Error("")
      this.archivioTarget.replaceWith(nuovo)
      if (this.hasFaseTarget) this.faseTarget.value = fase
      if (this.hasFiltroTarget) this.filtroTarget.value = filtro
      this.sommarioTarget.textContent = nuovo.dataset.sommario || ""
      this.scelti.clear()
    } catch (e) {
      if (this.vivo) this.mostraErrore(e.message || INVENTARIO)
    } finally {
      if (this.vivo) {
        this.lettura = false
        this.occupato(false)
        this.aggiorna()
      }
    }
  }

  async importa() {
    const fase = this.hasFaseTarget ? this.faseTarget.value : ""
    if (!fase || !this.scelti.size) return
    this.occupato(true)
    this.mostraErrore("")
    let cambiato = false
    try {
      const daFare = this.membroTargets.filter((li) => this.scelti.has(this.indice(li)) && this.usabile(li))
      for (let i = 0; i < daFare.length; i++) {
        if (!this.vivo) break
        const li = daFare[i]
        this.mostraAvanzamento(`${i + 1} di ${daFare.length}: ${li.dataset.nome}`)
        await this.invia(this.importaUrlValue, this.corpo({ indice: this.indice(li), sha256_membro: li.dataset.sha256, fase }), "application/json")
        cambiato = true
        if (this.vivo) {
          this.acquisiti.add(this.indice(li))
          this.scelti.delete(this.indice(li))
          this.aggiorna()
        }
      }
    } catch (e) {
      if (this.vivo) this.mostraErrore(e.message || INTERROTTA)
    } finally {
      if (this.vivo) {
        this.occupato(false)
        this.mostraAvanzamento("")
        this.aggiorna()
        if (cambiato) {
          try {
            await this.rileggiScheda()
          } catch {
            if (this.vivo) this.mostraErrore(NON_RILETTA)
          }
        }
      }
    }
  }

  async rileggiScheda() {
    const risposta = await fetch(this.schedaUrlValue, { headers: { Accept: "text/vnd.turbo-stream.html" } })
    if (!risposta.ok || risposta.redirected || !(risposta.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("scheda")
    Turbo.renderStreamMessage(await risposta.text())
  }
}
